import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, X, Sparkles, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { authenticatedFetch } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Next step suggestions based on user intent
const nextStepSuggestions = {
  resume_focused: {
    title: 'Search for Jobs',
    description: 'Now that your resume is ready, find opportunities that match your skills.',
    route: '/jobs',
    action_key: 'job_search'
  },
  clarity_seeker: {
    title: 'Build Your Resume',
    description: 'With clarity on your direction, create a resume that highlights your strengths.',
    route: '/resume/upload',
    action_key: 'resume_builder'
  },
  job_searcher: {
    title: 'Prepare for Interviews',
    description: 'Practice with AI mock interviews to feel confident when you land interviews.',
    route: '/interview-prep',
    action_key: 'interview_prep'
  },
  interview_prep: {
    title: 'Track Your Applications',
    description: 'Stay organized as you apply to opportunities with our job tracker.',
    route: '/job-tracker',
    action_key: 'job_tracker'
  }
};

/**
 * Post-Step Encouragement Component
 * 
 * Shows a congratulatory message after user completes their first recommended action.
 * Non-intrusive, dismissible, and provides optional next step.
 */
const PostStepEncouragement = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const checkProgress = React.useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/gps/progress`);
      if (response.ok) {
        const data = await response.json();
        setProgress(data);
        
        // Show encouragement if first action completed and not yet shown
        if (data.show_encouragement) {
          setVisible(true);
        }
      }
    } catch (error) {
      console.debug('Failed to check progress:', error);
    }
  }, []);

  useEffect(() => {
    checkProgress();
  }, [checkProgress]);

  const handleDismiss = async () => {
    setDismissed(true);
    setVisible(false);
    
    // Mark encouragement as shown in backend
    try {
      await authenticatedFetch(`${API_URL}/api/gps/dismiss-encouragement`, {
        method: 'POST'
      });
    } catch (error) {
      console.debug('Failed to dismiss encouragement:', error);
    }
  };

  const handleNextStep = async () => {
    const suggestion = nextStepSuggestions[progress?.user_intent] || nextStepSuggestions.resume_focused;
    
    // Track the next action
    try {
      await authenticatedFetch(`${API_URL}/api/gps/track-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_key: suggestion.action_key })
      });
    } catch (error) {
      console.debug('Failed to track action:', error);
    }
    
    handleDismiss();
    navigate(suggestion.route);
  };

  if (!visible || dismissed) {
    return null;
  }

  const suggestion = nextStepSuggestions[progress?.user_intent] || nextStepSuggestions.resume_focused;

  return (
    <div className="mb-6 animate-in slide-in-from-top duration-500">
      <div className="relative overflow-hidden bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-6 text-white shadow-lg">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 opacity-10">
          <Trophy className="w-32 h-32 -mt-4 -mr-4" />
        </div>
        
        {/* Close button */}
        <button 
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Content */}
        <div className="relative">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold">Nice work, you&apos;ve officially started.</h2>
          </div>
          
          <p className="text-green-100 mb-4">
            Most people never take this first step. You did.
          </p>
          
          <p className="text-white/90 mb-6">
            This is how progress happens, one clear step at a time.
          </p>
          
          {/* Next Step Suggestion */}
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 mb-4">
            <p className="text-green-100 text-sm mb-2">When you&apos;re ready, here&apos;s a good next move:</p>
            <h3 className="text-lg font-semibold mb-1">{suggestion.title}</h3>
            <p className="text-green-100 text-sm">{suggestion.description}</p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={handleNextStep}
              className="bg-white text-green-600 hover:bg-green-50"
            >
              Continue When Ready
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <button 
              onClick={handleDismiss}
              className="px-4 py-2 text-green-100 hover:text-white transition-colors"
            >
              I&apos;ll explore on my own
            </button>
          </div>
          
          {/* Progress Signal */}
          <div className="mt-4 flex items-center gap-2 text-green-200 text-sm">
            <Sparkles className="w-4 h-4" />
            <span>Progress: {progress?.actions_completed || 1} step completed</span>
          </div>
          
          <p className="text-green-200 text-xs mt-2">
            You&apos;re free to explore at your own pace. Nothing is required.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PostStepEncouragement;
